import { ChangeDetectionStrategy, Component, computed, effect, inject, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { IndicatorsService } from '../../services/indicators.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { RouteDataService } from '../../services/route-data.service';
import { Point, ItemStatus } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { MapMarker } from '@angular/google-maps';
import { MapService } from '../../services/map.service';
import { Subscription } from 'rxjs';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { START_POINT_FLAG, END_POINT_FLAG, ITEM_DEFAULT, ITEM_SUCCESS, ITEM_SUCCESS_DISABLED } from './config/constants';

@Component({
    selector: 'map-markers',
    standalone: true,
    imports: [ 
        CommonModule, 
        GoogleMapsModule,
    ],
    templateUrl: './markers.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MapMarkersComponent {
    @ViewChildren('markerRef', { read: MapMarker }) markers!: QueryList<MapMarker>;
    private markersChangesSub?: Subscription;
    private clusterer?: MarkerClusterer;

    private _routeDataService: RouteDataService = inject(RouteDataService);
    private _indicatorService: IndicatorsService = inject(IndicatorsService);
    private _breakpointsService: BreakpointsService = inject(BreakpointsService);
    private _mapService: MapService = inject(MapService);

    private isMobile = computed<boolean>(() => this._breakpointsService.isMobile())

    // Start Point ----------------------------------------------------------------------------------------------------------------
    public startPoint = computed<google.maps.LatLngLiteral>(() => this._routeDataService.startPoint());
    public startPointIconOptions: google.maps.MarkerOptions = START_POINT_FLAG();

    // End Point ------------------------------------------------------------------------------------------------------------------
    public endPoint = computed<google.maps.LatLngLiteral>(() => this._routeDataService.endPoint());
    public endPointIconOptions: google.maps.MarkerOptions = END_POINT_FLAG()

    // Markers --------------------------------------------------------------------------------------------------------------------
    public routeSegments = computed<Point[]>(() => this._routeDataService.getCurrentRouteDay());
    public successMarkerIconOptions: google.maps.MarkerOptions = ITEM_SUCCESS();
    private successMarkerIconOptionsDisabled: google.maps.MarkerOptions = ITEM_SUCCESS_DISABLED();

    private stepProgress = computed<number>(() => this._indicatorService.getStepProgress());

    async ngAfterViewInit() {
        const map = this._mapService.map.googleMap!;
        this.clusterer = new MarkerClusterer({ 
            map, 
            markers: [], 
            algorithm: new SuperClusterAlgorithm({ maxZoom: 20, radius: 60 })
        });
        
        this.markersChangesSub = this.markers.changes.subscribe(async () => {
            this.setMarkerClustered();            
        });
    }

    async setMarkerClustered(){
        this.clusterer?.clearMarkers();
        const gmMarkers = await Promise.all(this.markers.map(m => m._resolveMarker()));
        gmMarkers.forEach((element, index) => {
            const item = this.routeSegments()?.[index];
            const options = this.computeOptions(item, index);
            element.setPosition(item.currentLocation);
            element.setOptions(options);
            this.clusterer.addMarker(element);
        });

        const groups = this.groupNearbyMarkers(gmMarkers, 10);
        groups.forEach(g => {
            if (g.length > 1) {
                this.spiderfyCluster(g, 0.5);
            }
        });
    }

    public getMarkerInfo(item: Point): void{
        if (!item?.id)
            return;

        if (this.isMobile())
            this._indicatorService.openBottomSheetDetailsMobile(item.id, item.type)
        else
            this._indicatorService.openBottomSheetDetailsDesktop(item.id, item.type)
    }

    public isSuccessIcon(item){
        return ((this.stepProgress() >= 100) && 
            item.status === ItemStatus.RUNNING) && 
            !this._routeDataService.isYesterdayOrOlder(item.endDate)
    }

    public isFinishedIcon(item){
        return !this.isSuccessIcon(item) && item.status === ItemStatus.FINISHED;
    }

    private computeOptions(item: Point, index: number): google.maps.MarkerOptions {
        if (this.stepProgress() >= 100 && item.status === ItemStatus.RUNNING && !this._routeDataService.isYesterdayOrOlder(item.endDate))
            return this.successMarkerIconOptions;
        if (item.status === ItemStatus.FINISHED)
            return this.successMarkerIconOptionsDisabled;
        return ITEM_DEFAULT(index + 1);
    }

    private haversineDistance(p1, p2) {
        const R = 6371000;
        const dLat = (p2.lat - p1.lat) * Math.PI / 180;
        const dLng = (p2.lng - p1.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2 +
                    Math.cos(p1.lat*Math.PI/180) * Math.cos(p2.lat*Math.PI/180) *
                    Math.sin(dLng/2)**2;
        return 2 * R * Math.asin(Math.sqrt(a));
    }

    private groupNearbyMarkers(markers, maxDistanceMeters = 10) {
        const groups = [];
        const used = new Set();

        markers.forEach((m, i) => {
            if (used.has(i)) 
                return;
            
            const cluster = [m];
            for (let j = i + 1; j < markers.length; j++) {
                if (used.has(j)) 
                    continue;

                const d = this.haversineDistance(
                    m.getPosition().toJSON(),
                    markers[j].getPosition().toJSON()
                );

                if (d < maxDistanceMeters) {
                    cluster.push(markers[j]);
                    used.add(j);
                }
            }
            
            used.add(i);
            groups.push(cluster);
        });

        return groups;
    }

    private spiderfyCluster(markers, radiusMeters = 10) {
        if (!markers?.length) 
            return;

        const center = markers[0].getPosition();
        const count = markers.length;

        const latRad = center.lat() * Math.PI / 180;
        const metersPerDegLat = 111320;
        const metersPerDegLng = 111320 * Math.cos(latRad); 

        const dLat = radiusMeters / metersPerDegLat;
        const dLng = radiusMeters / metersPerDegLng;
        const startAngle = (count === 2) ? Math.PI / 2 : -Math.PI / 2;
        const angleStep = (2 * Math.PI) / count;

        markers.forEach((m, i) => {
            const angle = startAngle + i * angleStep;
            const lat = center.lat() + dLat * Math.cos(angle);
            const lng = center.lng() + dLng * Math.sin(angle);
            m.setPosition({ lat, lng });
        });
    }

    ngOnDestroy() {
        this.clusterer?.setMap(null);
        this.markersChangesSub?.unsubscribe();
        this.markersChangesSub = undefined;
    }
    
}